import React from 'react';
import Select, { components } from 'react-select';
import { motion, AnimatePresence } from 'framer-motion';

const CustomSelect = ({
  id,
  name,
  options,
  value,
  onChange,
  placeholder = '',
  leftIcon = null,
  className = '',
  ...props
}) => {
  // Custom ValueContainer to inject leftIcon
  const ValueContainer = ({ children, ...props }) => (
    <components.ValueContainer {...props}>
      <div className="flex items-center w-full">
        {leftIcon && (
          <span className="flex items-center mr-2 text-gray-500 text-lg">
            {leftIcon}
          </span>
        )}
        <div className="flex-1 flex items-center">{children}</div>
      </div>
    </components.ValueContainer>
  );

  // Custom Menu with framer-motion animation
  const menuMotion = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.18, ease: 'easeOut' },
  };
  const Menu = (props) => (
    <AnimatePresence mode="wait">
      <motion.div
        key="menu-motion"
        initial={menuMotion.initial}
        animate={
          props.selectProps.menuIsOpen ? menuMotion.animate : menuMotion.exit
        }
        exit={menuMotion.exit}
        transition={menuMotion.transition}
        style={{
          originY: 0,
          position: 'absolute',
          left: 0,
          right: 0,
          zIndex: 50,
        }}
      >
        {props.selectProps.menuIsOpen && <components.Menu {...props} />}
      </motion.div>
    </AnimatePresence>
  );

  const { components: customComponents = {}, ...restProps } = props;

  return (
    <div className={`relative ${className}`}>
      <Select
        isSearchable={false}
        id={id}
        name={name}
        options={options.filter((option) => option.value !== value)}
        value={
          value === '' ? null : options.find((option) => option.value === value)
        }
        onChange={(option) => onChange(option ? option.value : '')}
        placeholder={placeholder}
        isOptionDisabled={(option) => option.isDisabled}
        classNamePrefix="react-select"
        styles={{
          control: (provided, state) => ({
            ...provided,
            minHeight: '40px',
            borderRadius: '0.5rem',
            boxShadow: 'none',
            cursor: 'pointer',
            borderColor: state.isFocused
              ? '#60a5fa' // azul-300 cuando haces click (focus)
              : state.isHovered
              ? '#bfdbfe' // azul-200 cuando pasas el mouse (hover)
              : state.hasValue
              ? '#bfdbfe' // azul-600 si hay opción seleccionada
              : '#d1d5db', // gris por defecto
            paddingLeft: leftIcon ? '0.5rem' : undefined,
          }),
          placeholder: (provided) => ({
            ...provided,
            color: '#d1d5db', // gray-300
          }),
          singleValue: (provided) => ({
            ...provided,
            color: '#6b7280', // gray-500
          }),
          option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? '#374151' : '#6b7280', // gray-700 or gray-500
            backgroundColor: state.isSelected
              ? '#bfdbfe' // blue-200 para opción seleccionada
              : state.isFocused
              ? '#f3f4f6'
              : '#fff',
            cursor: 'pointer',
            ...(state.isActive && {
              backgroundColor: '#bfdbfe', // blue-300 cuando se hace click
            }),
          }),
          menu: (provided) => ({
            ...provided,
          }),
        }}
        components={{ ...customComponents, ValueContainer, Menu }}
        {...restProps}
      />
    </div>
  );
};

export default CustomSelect;
